import {useRef, useState} from "react";
import Modal from "../general/Modal";
import {useApi} from "../../api";
import './BulkRename.scss'

type Props = {
    files: VideoFile[]
    onClose: () => void
    onRenamed: () => void
}

function applyPattern(pattern: string, file: VideoFile, index: number, startIndex: number): string {
    const dotIdx = file.name.lastIndexOf('.')
    const name  = dotIdx >= 0 ? file.name.slice(0, dotIdx) : file.name
    const ext   = dotIdx >= 0 ? file.name.slice(dotIdx) : ''
    const dir   = file.directory?.name || ''
    const n     = index + startIndex

    return pattern
        .replace(/\{index:(\d+)\}/g, (_, pad) => String(n).padStart(parseInt(pad, 10), '0'))
        .replace(/\{index\}/g, String(n))
        .replace(/\{name\}/g, name)
        .replace(/\{ext\}/g, ext)
        .replace(/\{dir\}/g, dir)
}

const TOKENS = [
    { label: '{name}',    hint: 'original name without extension' },
    { label: '{ext}',     hint: 'extension (e.g. .mp4)' },
    { label: '{index}',   hint: 'sequential number' },
    { label: '{index:3}', hint: 'zero-padded (001, 002…)' },
    { label: '{dir}',     hint: 'parent folder name' },
]

export default ({ files, onClose, onRenamed }: Props) => {
    const api = useApi()
    const patternRef = useRef<HTMLInputElement>(null)

    const [pattern,    setPattern]    = useState('{name}{ext}')
    const [startIndex, setStartIndex] = useState(1)
    const [saving,     setSaving]     = useState(false)
    const [errors,     setErrors]     = useState<Record<string, string>>({})

    const previews = files.map((file, i) => ({
        file,
        newName: applyPattern(pattern, file, i, startIndex),
    }))

    const duplicateNames = previews
        .map(p => p.newName)
        .filter((n, i, arr) => arr.indexOf(n) !== i)

    const insertToken = (token: string) => {
        const el = patternRef.current
        if (!el) { setPattern(p => p + token); return }
        const start = el.selectionStart ?? pattern.length
        const end   = el.selectionEnd   ?? pattern.length
        const next  = pattern.slice(0, start) + token + pattern.slice(end)
        setPattern(next)
        requestAnimationFrame(() => {
            el.focus()
            el.setSelectionRange(start + token.length, start + token.length)
        })
    }

    const apply = async () => {
        if (duplicateNames.length > 0) return
        setSaving(true)
        setErrors({})

        const unchanged = previews.filter(p => p.newName === p.file.name)
        const toRename  = previews.filter(p => p.newName !== p.file.name)

        if (toRename.length === 0) { onClose(); return }

        const res = await api.renameFiles(toRename.map(p => ({ hash: p.file.hash, newName: p.newName })))

        const newErrors: Record<string, string> = {}
        for (const r of res.results) {
            if (!r.success) newErrors[r.hash] = r.error || 'rename failed'
        }

        setSaving(false)
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
        } else {
            onRenamed()
            onClose()
        }
    }

    const canApply = !saving && duplicateNames.length === 0
        && previews.some(p => p.newName !== p.file.name)

    return (
        <Modal title={`Rename ${files.length} file${files.length !== 1 ? 's' : ''}`} onClose={onClose}>
            <div className="br-root">
                <div className="br-pattern-row">
                    <input
                        ref={patternRef}
                        className="br-pattern-input"
                        value={pattern}
                        onChange={e => setPattern(e.target.value)}
                        placeholder="e.g. {dir}_{index:3}{ext}"
                        disabled={saving}
                        spellCheck={false}
                    />
                    <label className="br-start-label">
                        Start #
                        <input
                            type="number"
                            className="br-start-input"
                            min={0}
                            value={startIndex}
                            onChange={e => setStartIndex(parseInt(e.target.value, 10) || 0)}
                            disabled={saving}
                        />
                    </label>
                </div>

                <div className="br-tokens">
                    {TOKENS.map(t => (
                        <button
                            key={t.label}
                            className="br-token"
                            title={t.hint}
                            onClick={() => insertToken(t.label)}
                            disabled={saving}
                        >{t.label}</button>
                    ))}
                </div>

                {duplicateNames.length > 0 && (
                    <div className="br-warning">
                        Pattern produces duplicate names — add {'{index}'} to make them unique.
                    </div>
                )}

                <div className="br-preview">
                    <div className="br-preview-header">
                        <span>Current name</span>
                        <span>New name</span>
                    </div>
                    <div className="br-preview-rows">
                        {previews.map(({ file, newName }) => {
                            const isDup     = duplicateNames.includes(newName)
                            const unchanged = newName === file.name
                            const hasError  = !!errors[file.hash]
                            return (
                                <div
                                    key={file.hash}
                                    className={
                                        'br-preview-row' +
                                        (isDup     ? ' br-dup'     : '') +
                                        (unchanged ? ' br-same'    : '') +
                                        (hasError  ? ' br-error'   : '')
                                    }
                                >
                                    <span className="br-old" title={file.name}>{file.name}</span>
                                    <span className="br-arrow">→</span>
                                    <span className="br-new" title={newName}>
                                        {newName || <em>empty</em>}
                                        {hasError && <span className="br-err-msg">{errors[file.hash]}</span>}
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                </div>

                <div className="br-footer">
                    <span className="br-summary">
                        {previews.filter(p => p.newName !== p.file.name).length} of {files.length} will be renamed
                    </span>
                    <button className="br-apply" onClick={apply} disabled={!canApply}>
                        {saving ? 'Renaming…' : 'Apply'}
                    </button>
                </div>
            </div>
        </Modal>
    )
}
